import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchCategories, fetchEmailBody } from "../services/emailService";
import EmailAttachments from "../components/EmailAttachments";
import { filterCategories } from "../utils/search";

const tone = (badge) => ({ bg: `${badge}1a`, border: badge, badge, text: "#fff" });

export const CATEGORY_COLORS = {
  "Urgent":        { bg: "#fff0f0", border: "#e74c3c", badge: "#e74c3c", text: "#fff" },
  "Sécurité":      { bg: "#fff8f0", border: "#e67e22", badge: "#e67e22", text: "#fff" },
  "Finance":       { bg: "#f0fff4", border: "#27ae60", badge: "#27ae60", text: "#fff" },
  "RH":            { bg: "#f0f4ff", border: "#2980b9", badge: "#2980b9", text: "#fff" },
  "Réunion":       { bg: "#f8f0ff", border: "#8e44ad", badge: "#8e44ad", text: "#fff" },
  "Académique":    { bg: "#f0faff", border: "#0984e3", badge: "#0984e3", text: "#fff" },
  "Support":       { bg: "#fffdf0", border: "#f39c12", badge: "#f39c12", text: "#fff" },
  "Livraison":     { bg: "#f0fffd", border: "#00b894", badge: "#00b894", text: "#fff" },
  "Promotions":    { bg: "#fdf0ff", border: "#6c5ce7", badge: "#6c5ce7", text: "#fff" },
  "Marketing":     { bg: "#fdf0ff", border: "#6c5ce7", badge: "#6c5ce7", text: "#fff" },
  "Notifications": { bg: "#f0fbff", border: "#00bcd4", badge: "#00bcd4", text: "#fff" },
  "Général":       { bg: "#f6f6f7", border: "#95a5a6", badge: "#95a5a6", text: "#fff" },
  "General":       tone("#95a5a6"),
  "Session":       tone("#0984e3"),
  "Exemption":     tone("#6c5ce7"),
  "Academic Advising": tone("#00b894"),
  "Deadline":      tone("#e67e22"),
  "Registration":  tone("#2980b9"),
  "Documents":     tone("#8e44ad"),
  "Scholarship":   tone("#27ae60"),
  "Schedule Conflict": tone("#e74c3c"),
  "Reserved Seats": tone("#d35400"),
  "Grade Appeal":  tone("#c0392b"),
  "Letter of Permission": tone("#16a085"),
  "Microprogram":  tone("#2d98da"),
  "Withdrawal":    tone("#b33771"),
  "Study Interruption": tone("#f39c12"),
  "Equivalences":  tone("#4b7bec"),
  "Graduation":    tone("#20bf6b"),
  "Internship":    tone("#0fb9b1"),
  "Program Change": tone("#a55eea"),
  "Deferred Exam": tone("#eb3b5a"),
  "Academic Standing": tone("#fa8231"),
};

const DEFAULT_COLOR = { bg: "#f6f6f7", border: "#95a5a6", badge: "#888", text: "#fff" };
export function getColor(name) { return CATEGORY_COLORS[name] || DEFAULT_COLOR; }

export const CATEGORY_TRANSLATIONS = {
  "Urgent": { fr: "Urgent", en: "Urgent" },
  "Sécurité": { fr: "Sécurité", en: "Security" },
  "Finance": { fr: "Finance", en: "Finance" },
  "RH": { fr: "RH", en: "HR" },
  "Réunion": { fr: "Réunion", en: "Meeting" },
  "Académique": { fr: "Académique", en: "Academic" },
  "Support": { fr: "Support", en: "Support" },
  "Livraison": { fr: "Livraison", en: "Delivery" },
  "Promotions": { fr: "Promotions", en: "Promotions" },
  "Marketing": { fr: "Marketing", en: "Marketing" },
  "Notifications": { fr: "Notifications", en: "Notifications" },
  "Général": { fr: "Général", en: "General" },
  "General": { fr: "Général", en: "General" },
  "Session": { fr: "Cheminement", en: "Study path" },
  "Exemption": { fr: "Exemption", en: "Exemption" },
  "Academic Advising": { fr: "Conseil académique", en: "Academic advising" },
  "Deadline": { fr: "Date limite", en: "Deadline" },
  "Registration": { fr: "Inscription", en: "Registration" },
  "Documents": { fr: "Documents", en: "Documents" },
  "Scholarship": { fr: "Bourses", en: "Scholarships" },
  "Schedule Conflict": { fr: "Conflit d'horaire", en: "Schedule conflict" },
  "Reserved Seats": { fr: "Sièges réservés", en: "Reserved seats" },
  "Grade Appeal": { fr: "Contestation de note", en: "Grade appeal" },
  "Letter of Permission": { fr: "Lettre de permission", en: "Letter of permission" },
  "Microprogram": { fr: "Microprogramme", en: "Microprogram" },
  "Withdrawal": { fr: "Abandon de cours", en: "Course withdrawal" },
  "Study Interruption": { fr: "Interruption des études", en: "Study interruption" },
  "Equivalences": { fr: "Équivalences", en: "Equivalences" },
  "Graduation": { fr: "Diplomation", en: "Graduation" },
  "Internship": { fr: "Stage", en: "Internship" },
  "Program Change": { fr: "Changement de programme", en: "Program change" },
  "Deferred Exam": { fr: "Examen différé", en: "Deferred exam" },
  "Academic Standing": { fr: "Statut académique", en: "Academic standing" },
};

export function getCategoryTranslation(categoryName, lang) {
  const translation = CATEGORY_TRANSLATIONS[categoryName];
  return translation ? translation[lang] : categoryName;
}

function BodyRenderer({ body }) {
  const { t } = useTranslation();
  const iframeRef = useRef(null);
  const isHtml = Boolean(body) && body.trim().startsWith("<");

  function onLoad() {
    if (!iframeRef.current) return;
    try {
      const h = iframeRef.current.contentDocument?.body?.scrollHeight;
      if (h) iframeRef.current.style.height = Math.min(h + 30, 600) + "px";
    } catch (_) {}
  }

  if (isHtml) {
    return (
      <iframe
        ref={iframeRef}
        srcDoc={body}
        sandbox="allow-same-origin"
        onLoad={onLoad}
        className="email-html-frame"
        title="Email content"
      />
    );
  }
  return <pre className="body">{body || t('categories.bodyNotAvailable')}</pre>;
}

function EmailModal({ email, onClose }) {
  const { t, i18n } = useTranslation();
  const color = getColor(email.category);
  const [body, setBody] = useState(email.body || "");

  useEffect(() => {
    if (body) return;
    let cancelled = false;
    fetchEmailBody(email.id)
      .then((b) => { if (!cancelled) setBody(b || ""); })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email.id]);

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  function formatDate(dateStr) {
    const date = new Date(dateStr);
    if (i18n.language === "fr") {
      return date.toLocaleDateString("fr-CA");
    } else {
      return date.toISOString().split("T")[0];
    }
  }

  return (
    <div className="cat-modal-backdrop" onClick={handleBackdrop}>
      <div className="cat-modal">
        <div className="cat-modal-header">
          <div className="cat-modal-title">
            <span
              className="category-badge"
              style={{ background: color.badge, color: color.text, fontSize: 11 }}
            >
              {getCategoryTranslation(email.category, i18n.language)}
            </span>
            <h3>{email.subject || t('categories.noSubject')}</h3>
          </div>
          <button className="categories-close-btn" onClick={onClose}>{t('common.close')}</button>
        </div>
        <div className="cat-modal-meta">
          <span className="from">{email.from}</span>
          <span className="date">{formatDate(email.date)}</span>
        </div>
        <EmailAttachments emailId={email.id} />
        <BodyRenderer body={body} />
      </div>
    </div>
  );
}

export default function CategoriesPage() {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [modal, setModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchCategories()
      .then((data) => {
        if (!cancelled) setCategories(data.categories || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || t('categories.error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [t]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") setModal(null); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function formatDate(dateStr) {
    const date = new Date(dateStr);
    if (i18n.language === "fr") {
      return date.toLocaleDateString("fr-CA");
    } else {
      return date.toISOString().split("T")[0];
    }
  }

  const total = categories.reduce((sum, c) => sum + c.count, 0);

  const filteredCategories = useMemo(() => {
    return filterCategories(categories, searchTerm);
  }, [categories, searchTerm]);

  return (
    <>
      <section className="categories-page">
        <div className="categories-header">
          <h2>{t('categories.title')}</h2>
          <input
            type="text"
            className="search-input"
            placeholder={t('email.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading && <p className="categories-status">{t('categories.loading')}</p>}
        {error  && <p className="categories-status categories-error">{error}</p>}

        {!loading && !error && (
          <div className="categories-accordion">
            {filteredCategories.length === 0 ? (
              <p className="no-results">{t('email.noResults')}</p>
            ) : (
              filteredCategories.map((cat) => {
              const color = getColor(cat.name);
              const isOpen = expanded === cat.name;
              const emailCount = cat.emails.length;
              const pct = total > 0 ? Math.round((emailCount / total) * 100) : 0;

              return (
                <div
                  key={cat.name}
                  className="cat-section"
                  style={{ borderLeft: `4px solid ${color.border}` }}
                >
                  <button
                    className="cat-section-header"
                    onClick={() => setExpanded(isOpen ? null : cat.name)}
                    aria-expanded={isOpen}
                  >
                    <span
                      className="category-badge"
                      style={{ background: color.badge, color: color.text }}
                    >
                      {getCategoryTranslation(cat.name, i18n.language)}
                    </span>
                    <span className="cat-section-count">
                      {emailCount === 1 
                        ? t('categories.emailCount_one', { count: emailCount }) 
                        : t('categories.emailCount_other', { count: emailCount })}
                    </span>
                    <div className="category-bar-wrap cat-section-bar">
                      <div
                        className="category-bar"
                        style={{ width: `${pct}%`, background: color.border }}
                      />
                    </div>
                    <span className="cat-section-pct">{pct}%</span>
                    <span className="cat-section-arrow">{isOpen ? "▲" : "▼"}</span>
                  </button>

                  {isOpen && (
                    <ul className="category-email-list cat-section-list">
                      {cat.emails.map((email) => (
                          <li
                            key={email.id}
                            className="category-email-item"
                            onClick={() => setModal(email)}
                            title={t('categories.clickToRead')}
                          >
                            <div className="category-email-subject">
                              {email.subject || t('categories.noSubject')}
                            </div>
                            <div className="category-email-meta">
                              <span className="category-email-from">{email.from}</span>
                              <span className="category-email-date">
                                {formatDate(email.date)}
                              </span>
                            </div>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              );
            }))}
          </div>
        )}
      </section>

      {modal && <EmailModal email={modal} onClose={() => setModal(null)} />}
    </>
  );
}
